import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { db } from '@core/db/db'
import type { PosShift } from '@core/db/models'
import { META_CATALOG_SYNCED_AT } from '@core/db/schema'
import { money } from '@core/utils/format'
import { pluralRuCount } from '@core/utils/plural'
import { httpErrorText } from '@core/utils/httpError'
import { syncService } from '@core/services/sync/SyncService'
import { shortId } from '@core/utils/deviceLabel'
import { btn, btnBase, btnSize, cx, hint } from '@ui/classes'
import { errBanner, seg, segActive, segBar, segIdle, sectionCard } from '@ui/admin'
import type { AdminSectionProps } from './types'
import { CLOUD_API, CLOUD_NOT_CONNECTED, deviceBadge } from './shared'
import { useAuthToken } from './useAuthToken'

/** Режим периода «Обзора» (ARCH-TASK-62). */
type Period = 'shift' | 'today' | 'week' | 'month'

const PERIOD_LABEL: Record<Period, string> = {
  shift: 'Смена',
  today: 'Сегодня',
  week: '7 дней',
  month: '30 дней'
}

/** KPI за период + предыдущий период той же длины. */
interface OverviewKpi {
  /** Нетто-выручка: продажи минус возвраты (ARCH-TASK-65). */
  revenue: number
  /** Выручка продаж без возвратов. */
  sales_revenue?: number
  /** Сумма возвратов (положительная). */
  returns_total?: number
  returns_count?: number
  receipts: number
  avg_check: number
  items: number
  /** 65.10: нефискальные чеки и списания. */
  non_fiscal_revenue?: number
  non_fiscal_count?: number
  non_fiscal_marked_total?: number
  non_fiscal_marked_count?: number
  marking_skipped_count?: number
  held_total?: number
  held_count?: number
  valid_ready_count?: number
  printed_count?: number
  cancelled_total?: number
  cancelled_count?: number
  print_failures_count?: number
  hold_aging_max_sec?: number
  hold_aging_avg_sec?: number
  blocked_shifts?: number
  writeoffs_total?: number
  writeoffs_count?: number
  writeoffs_hold_total?: number
  writeoffs_hold_count?: number
  prev: {
    revenue: number
    sales_revenue?: number
    returns_total?: number
    returns_count?: number
    receipts: number
    avg_check: number
    items: number
  }
}

/** Ответ облачной сводки. */
interface Overview {
  range: {
    from: string
    to: string
    prev_from: string
    prev_to: string
    timezone: string
    zone_id: string | null
  }
  kpi: OverviewKpi
  by_day: { day: string; revenue: number; receipts: number; returns_total?: number }[]
  top_items: { name: string; qty: number; revenue: number }[]
  by_payment: { method: string; revenue: number; receipts: number; returns_total?: number }[]
  by_cashier: { cashier: string; revenue: number; receipts: number; returns_total?: number }[]
  by_device: { device_id: string; revenue: number; receipts: number }[]
  by_hour: { hour: number; revenue: number; receipts: number }[]
  corrections: {
    count: number
    total: number
    /** Разбивка по признаку расчёта (ARCH-TASK-65.6). */
    income_count?: number
    income_total?: number
    returns_count?: number
    returns_total?: number
  }
  /** «Оборот кассы»: нетто выручки + коррекции прихода − коррекции-возвраты. */
  cash_turnover?: number
}

/** Оперативное состояние кассы — читается локально, работает офлайн. */
interface LocalOps {
  shift: PosShift | null
  /** false — смена закрыта, показываем последнюю. */
  shiftOpen: boolean
  unsentReceipts: number
  /** Чеки, отклонённые облаком (422): не ретраятся, причина видна здесь. */
  rejectedReceipts: number
  lastRejectedReason: string | null
  offlineQueue: number
  productsLive: number
  activeVersion: number | null
  syncedAt: string | null
  lowStock: { name: string; stock: number; unit: string }[]
  /** Порог «заканчивается» (единиц товара) — из настроек магазина. */
  lowStockThreshold: number
  /** Сколько товаров имеют свой порог (для подписи блока). */
  lowStockOwnRules: number
  /** Ведётся ли учёт остатков: если у всех товаров 0 — блок «заканчиваются» скрыт. */
  stockTracked: boolean
}

const PAYMENT_LABEL: Record<string, string> = {
  cash: 'Наличные',
  card: 'Карта',
  sbp: 'СБП',
  unknown: 'Не указан (старые чеки)'
}

/** Кэш последней успешной сводки — чтобы «Обзор» не пустел без сети. */
const META_OVERVIEW_CACHE = 'overviewCache'

/** Границы периода: «Смена» — интервал смены, остальные — календарные. */
function periodRange(period: Period, shift: PosShift | null): { from: Date; to: Date } {
  const now = new Date()
  if (period === 'shift' && shift) {
    return {
      from: new Date(shift.openedAt),
      to: shift.closedAt ? new Date(shift.closedAt) : now
    }
  }
  if (period === 'today') {
    const from = new Date(now)
    from.setHours(0, 0, 0, 0)
    return { from, to: now }
  }
  const days = period === 'week' ? 7 : 30
  const from = new Date(now)
  from.setHours(0, 0, 0, 0)
  from.setDate(from.getDate() - (days - 1))
  return { from, to: now }
}

/** Дата и время без лишней запятой: «17.10 21:46». */
function clockText(ms: number): string {
  const date = new Date(ms)
  const day = `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  return `${day} ${time}`
}

/** Длительность смены: «18 ч 17 мин» / «47 мин». */
function durationText(from: number, to: number): string {
  const minutes = Math.max(0, Math.round((to - from) / 60000))
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return hours > 0 ? `${hours} ч ${String(rest).padStart(2, '0')} мин` : `${rest} мин`
}

/** Подпись периода — цифры без неё не показываем. */
function periodCaption(period: Period, shift: PosShift | null, data: Overview | null): string {
  if (period === 'shift') {
    // Смены нет — период фактически «сегодня», так и подписываем.
    if (!shift) return 'Смена не открыта — показан сегодняшний день'
    const zone = shift.zoneId ? ` · ${shortId(shift.zoneId)}` : ''
    return shift.closedAt
      ? `Смена №${shift.shiftNumber}${zone} · ${clockText(shift.openedAt)} → ${clockText(shift.closedAt)}`
      : `Смена №${shift.shiftNumber}${zone} · открыта ${clockText(shift.openedAt)}`
  }
  if (!data) return ''
  const from = new Date(data.range.from).toLocaleDateString('ru-RU')
  const to = new Date(new Date(data.range.to).getTime() - 1000).toLocaleDateString('ru-RU')
  return from === to ? from : `${from} — ${to}`
}

/** Подпись дня из 'YYYY-MM-DD' без UTC-сдвига (иначе в -TZ съедет на сутки). */
function dayLabel(day: string): string {
  const [, month, date] = day.split('-')
  return `${date}.${month}`
}

/**
 * Окно сравнения: тот же интервал в прошлом.
 *
 * «Сегодня» — сдвиг на сутки (вчера 00:00–16:36). Длинные периоды — равное
 * окно вплотную перед основным: сдвиг на `days` давал наложение для «30 дней»
 * (конец прошлого окна заходил внутрь текущего).
 */
function previousRange(period: Period, from: Date, to: Date): { from: Date; to: Date } {
  if (period === 'today') {
    // setDate корректно переживает переход на летнее/зимнее время.
    const prevFrom = new Date(from)
    prevFrom.setDate(prevFrom.getDate() - 1)
    const prevTo = new Date(to)
    prevTo.setDate(prevTo.getDate() - 1)
    return { from: prevFrom, to: prevTo }
  }
  const span = to.getTime() - from.getTime()
  return { from: new Date(from.getTime() - span), to: new Date(from) }
}

/** Дельта к предыдущему периоду: «▲ +12 %» или null, если сравнить не с чем. */
function delta(current: number, prev: number): { text: string; positive: boolean } | null {
  if (!Number.isFinite(prev) || prev <= 0) return null
  const percent = ((current - prev) / prev) * 100
  if (Math.abs(percent) < 0.5) return null
  return {
    text: `${percent > 0 ? '▲ +' : '▼ '}${percent.toFixed(0)} %`,
    positive: percent > 0
  }
}

/** Последняя закрытая смена своей зоны (когда открытой нет). */
async function lastClosedShift(): Promise<PosShift | null> {
  const zoneId = await syncService.ensureZoneId().catch(() => '')
  const closed = await db.shifts.where('status').equals('closed').toArray()
  const mine = closed.filter((s) => !s.zoneId || s.zoneId === zoneId)
  mine.sort((a, b) => (b.closedAt ?? b.openedAt) - (a.closedAt ?? a.openedAt))
  return mine[0] ?? null
}

/** Карточка KPI: значение, подпись и дельта к предыдущему периоду. */
function KpiCard({
  title,
  value,
  note,
  deltaInfo
}: {
  title: string
  value: string
  /** Пояснение под значением (например, «в т.ч. возвраты −500 ₽»). */
  note?: string
  /** undefined — сравнение не показываем; null — сравнивать не с чем («—»). */
  deltaInfo?: { text: string; positive: boolean } | null
}) {
  return (
    <div className="rounded-panel border border-line bg-white p-3">
      <div className="text-small text-muted">{title}</div>
      <div className="mt-1 text-big font-bold tabular-nums text-ink">{value}</div>
      {note && <div className="text-tiny text-muted">{note}</div>}
      {deltaInfo === undefined ? null : deltaInfo ? (
        <div
          className={cx(
            'text-small font-semibold',
            deltaInfo.positive ? 'text-green-700' : 'text-red-600'
          )}
        >
          {deltaInfo.text} <span className="font-normal text-muted">к пред.</span>
        </div>
      ) : (
        <div className="text-small text-muted">—</div>
      )}
    </div>
  )
}

/** Список с горизонтальными барами: топ-товары, оплата, кассиры, часы. */
function BarList({
  items
}: {
  items: { label: string; value: number; hint?: string }[]
}) {
  const max = Math.max(...items.map((item) => item.value), 1)
  return (
    <ul className="m-0 list-none">
      {items.map((item, index) => (
        // Ключ с индексом: подписи могут повторяться (две «Мобильная касса»),
        // а дубли ключей ломают реконсиляцию React и оставляют старые строки.
        <li key={`${item.label}-${index}`} className="py-1">
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="truncate text-ink">{item.label}</span>
            <span className="whitespace-nowrap tabular-nums text-ink">{money(item.value)}</span>
          </div>
          <div className="mt-0.5 h-1.5 rounded-pill bg-soft">
            <div
              className="h-1.5 rounded-pill bg-ink/70"
              style={{ width: item.value > 0 ? `${Math.max(2, (item.value / max) * 100)}%` : '0%' }}
            />
          </div>
          {item.hint && <div className="text-tiny text-muted">{item.hint}</div>}
        </li>
      ))}
    </ul>
  )
}


/** Локальный снимок оперативного состояния (без сети). */
async function readLocalOps(core: AdminSectionProps['core']): Promise<LocalOps> {
  const openShift = await core.shift.getActiveShift()
  const shift = openShift ?? (await lastClosedShift())
  // Один проход по очереди: «не отправлено» и «отклонено» считаем вместе —
  // sales_queue со временем растёт, двойной filter() её всю сканирует.
  const allQueued = await db.sales_queue.toArray()
  const unsentReceipts = allQueued.filter((s) => !s.is_synced).length
  const rejected = allQueued
    .filter((s) => Boolean(s.sync_error))
    .sort((a, b) => (b.sync_rejected_at ?? 0) - (a.sync_rejected_at ?? 0))
  const rejectedReceipts = rejected.length
  const lastRejectedReason = rejected[0]?.sync_error ?? null
  const offlineQueue = await db.offlineQueue.where('status').equals('pending').count()
  const activeVersion = await core.catalog.getActiveVersionId()
  const products =
    activeVersion != null
      ? await db.products.where('catalogVersionId').equals(activeVersion).toArray()
      : []
  const live = products.filter((product) => !product.isDeleted)
  // Порог «заканчивается» — из настроек магазина («Организация»), по умолчанию 3 ед.
  const lowStockThreshold = await core.catalog.getLowStockThreshold()
  // У товара может быть свой порог (ARCH-TASK-64a), иначе — общий по магазину.
  const thresholdOf = (product: { minStock?: number }) => product.minStock ?? lowStockThreshold
  const lowStock = live
    .filter((product) => product.stock <= thresholdOf(product))
    .sort((a, b) => a.stock - b.stock)
    .slice(0, 5)
    .map((product) => ({ name: product.name, stock: product.stock, unit: product.unit }))
  const syncedEntry = await db.meta.get(META_CATALOG_SYNCED_AT)

  return {
    shift,
    shiftOpen: Boolean(openShift),
    unsentReceipts,
    rejectedReceipts,
    lastRejectedReason,
    offlineQueue,
    productsLive: live.length,
    activeVersion,
    syncedAt: typeof syncedEntry?.value === 'string' ? syncedEntry.value : null,
    lowStock,
    lowStockThreshold,
    // Сколько товаров со своим порогом — для подписи блока.
    lowStockOwnRules: live.filter((product) => product.minStock != null).length,
    // Учёт остатков ведётся, если есть хоть один ненулевой остаток ИЛИ задан
    // порог товара: min_stock сам по себе доказывает, что остатки важны.
    stockTracked: live.some((product) => product.stock > 0 || product.minStock != null)
  }
}

/**
 * «Обзор» — главный экран директора (ARCH-TASK-62).
 *
 * Период по умолчанию — смена (или последняя закрытая): это Z-отчётная
 * единица, и её KPI считаются локально, мгновенно и офлайн. Остальные
 * периоды (сегодня/7/30) — облачные агрегаты по всем кассам.
 */
export function OverviewSection({ core }: AdminSectionProps) {
  const token = useAuthToken()
  const [period, setPeriod] = useState<Period>('shift')
  const [ops, setOps] = useState<LocalOps | null>(null)
  const [data, setData] = useState<Overview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [updatedAt, setUpdatedAt] = useState<number | null>(null)
  const [pendingCount, setPendingCount] = useState<number | null>(null)

  /** Номер последнего запроса: ответы устаревших периодов игнорируются. */
  const requestRef = useRef(0)

  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    []
  )

  /**
   * Перечитать локальное состояние и (если есть токен) облачную сводку.
   * `requestRef` отбрасывает ответы устаревших периодов — иначе быстрое
   * переключение «Сегодня → 7 дней» оставляет на экране прошлый запрос.
   */
  const reload = useCallback(async () => {
    const requestId = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      const local = await readLocalOps(core)
      if (requestId !== requestRef.current) return
      setOps(local)

      if (!token) {
        setData(null)
        return
      }

      const { from, to } = periodRange(period, local.shift)
      const prev = previousRange(period, from, to)
      const params = new URLSearchParams({
        from: from.toISOString(),
        to: to.toISOString(),
        prev_from: prev.from.toISOString(),
        prev_to: prev.to.toISOString(),
        tz: timezone
      })
      // «Смена» на мультизонной точке — только своя зона.
      if (period === 'shift' && local.shift?.zoneId) {
        params.set('zone_id', local.shift.zoneId)
      }

      const response = await fetch(`${CLOUD_API}/analytics/overview?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        throw new Error(httpErrorText('Сводка', response.status))
      }
      const json = (await response.json()) as Overview
      if (requestId !== requestRef.current) return
      setData(json)
      setUpdatedAt(Date.now())
      // Кэшируем успешный ответ: при обрыве связи покажем его с подписью времени.
      void db.meta
        .put({ key: META_OVERVIEW_CACHE, value: { data: json, period, at: Date.now() } })
        .catch(() => undefined)
    } catch (e: unknown) {
      if (requestId !== requestRef.current) return
      const message = e instanceof Error ? e.message : String(e)
      const cached = await db.meta.get(META_OVERVIEW_CACHE).catch(() => undefined)
      const value = cached?.value as
        | { data?: Overview; period?: Period; at?: number }
        | undefined
      if (value?.data && value.period === period && value.at) {
        setData(value.data)
        setUpdatedAt(value.at)
        const time = new Date(value.at).toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit'
        })
        setError(`${message} — показаны данные за «${PERIOD_LABEL[period]}» на ${time}`)
      } else {
        setError(message)
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false)
    }
  }, [core, period, timezone, token])

  useEffect(() => {
    void reload()
  }, [reload])

  // Заявки с касс на модерации — только когда есть облако.
  useEffect(() => {
    if (!token) {
      setPendingCount(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const response = await fetch(`${CLOUD_API}/catalog/pending?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!response.ok) return
        const json = (await response.json()) as { counts?: { pending?: number } }
        if (!cancelled) setPendingCount(json.counts?.pending ?? 0)
      } catch {
        // Офлайн — карточка заявок просто не покажет число.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token, updatedAt])

  const kpi = data?.kpi
  const caption = periodCaption(period, ops?.shift ?? null, data)
  const periodKeys: Period[] = ['shift', 'today', 'week', 'month']
  // В режиме смены предыдущее окно — не «прошлая смена», дельту не показываем.
  const showDelta = period !== 'shift'
  // Сколько выручки смены дали другие кассы зоны (сверка с локальной кассой).
  const zoneOtherRevenue =
    period === 'shift' && kpi && ops?.shift ? kpi.revenue - ops.shift.totalSales : 0

  /** Дни периода с нулями: 30-дневный график не должен «прыгать» через пустые дни. */
  const dayRows = useMemo(() => {
    if (!data) return []
    const byKey = new Map(data.by_day.map((row) => [row.day, row]))
    const start = new Date(data.range.from)
    start.setHours(0, 0, 0, 0)
    const end = new Date(data.range.to)
    const rows: { day: string; revenue: number; receipts: number; returns_total?: number }[] = []
    for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(
        cursor.getDate()
      ).padStart(2, '0')}`
      rows.push(byKey.get(key) ?? { day: key, revenue: 0, receipts: 0, returns_total: 0 })
    }
    return rows
  }, [data])

  return (
    <div className={sectionCard}>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="flex items-center gap-2">
          {updatedAt && (
            <span className={hint}>
              обновлено{' '}
              {new Date(updatedAt).toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          )}
          <button
            onClick={() => void reload()}
            disabled={loading}
            className={cx(btnBase, btn.secondary, btnSize.sm)}
          >
            {loading ? 'Обновление…' : 'Обновить'}
          </button>
        </div>
      </div>

      <div className={segBar} role="tablist" aria-label="Период обзора">
        {periodKeys.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={period === key}
            onClick={() => setPeriod(key)}
            className={cx(seg, period === key ? segActive : segIdle)}
          >
            {PERIOD_LABEL[key]}
          </button>
        ))}
      </div>

      {caption && <div className={hint}>{caption}</div>}
      {error && <p className={errBanner}>{error}</p>}

      {!token && <p className={hint}>{CLOUD_NOT_CONNECTED}</p>}

      {/* Без облака главный KPI смены всё равно доступен — считаем локально. */}
      {!token && ops?.shift && (
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <KpiCard title="Выручка смены (локально)" value={money(ops.shift.totalSales)} />
          <KpiCard title="Чеков" value={String(ops.shift.salesCount)} />
          <KpiCard
            title="Средний чек"
            value={money(
              ops.shift.salesCount > 0 ? ops.shift.totalSales / ops.shift.salesCount : 0
            )}
          />
        </div>
      )}

      {kpi && (
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <KpiCard
            title="Выручка"
            value={money(kpi.revenue)}
            note={
              (kpi.returns_total ?? 0) > 0
                ? `в т.ч. возвраты −${money(kpi.returns_total ?? 0)}`
                : undefined
            }
            deltaInfo={showDelta ? delta(kpi.revenue, kpi.prev.revenue) : undefined}
          />
          <KpiCard
            title="Возвраты"
            value={money(kpi.returns_total ?? 0)}
            note={(kpi.returns_count ?? 0) > 0 ? `${kpi.returns_count} шт.` : undefined}
            deltaInfo={
              showDelta ? delta(kpi.returns_total ?? 0, kpi.prev.returns_total ?? 0) : undefined
            }
          />
          <KpiCard
            title="Чеки без ККТ"
            value={money(kpi.non_fiscal_revenue ?? 0)}
            note={`${pluralRuCount(kpi.non_fiscal_count ?? 0, ['чек', 'чека', 'чеков'])} · без кода ${kpi.marking_skipped_count ?? 0}`}
          />
          <KpiCard
            title="Чеки на проверке маркировки"
            value={money(kpi.held_total ?? 0)}
            note={`${kpi.held_count ?? 0} ждут · готовы ${kpi.valid_ready_count ?? 0} · напечатано ${kpi.printed_count ?? 0}`}
          />
          <KpiCard
            title="Ошибки печати"
            value={String(kpi.print_failures_count ?? 0)}
            note="чеки на проверке с ошибкой ККТ"
          />
          <KpiCard
            title="Отменены"
            value={money(kpi.cancelled_total ?? 0)}
            note={pluralRuCount(kpi.cancelled_count ?? 0, ['чек', 'чека', 'чеков'])}
          />
          <KpiCard
            title="Возраст чеков на проверке"
            value={`${Math.round((kpi.hold_aging_max_sec ?? 0) / 3600)} ч`}
            note={`средний ${Math.round((kpi.hold_aging_avg_sec ?? 0) / 3600)} ч`}
          />
          <KpiCard
            title="Блокировано смен"
            value={String(kpi.blocked_shifts ?? 0)}
            note="ждут решения по маркировке"
          />
          <KpiCard
            title="Чеки без ККТ с кодом"
            value={money(kpi.non_fiscal_marked_total ?? 0)}
            note={pluralRuCount(kpi.non_fiscal_marked_count ?? 0, ['чек', 'чека', 'чеков'])}
          />
          <KpiCard
            title="Списания"
            value={money(kpi.writeoffs_total ?? 0)}
            note={`${pluralRuCount(kpi.writeoffs_count ?? 0, ['документ', 'документа', 'документов'])} · из чеков на проверке ${kpi.writeoffs_hold_count ?? 0}`}
          />
          <KpiCard
            title="Чеков"
            value={String(kpi.receipts)}
            deltaInfo={showDelta ? delta(kpi.receipts, kpi.prev.receipts) : undefined}
          />
          <KpiCard
            title="Средний чек"
            value={money(kpi.avg_check)}
            deltaInfo={showDelta ? delta(kpi.avg_check, kpi.prev.avg_check) : undefined}
          />
          <KpiCard
            title="Единиц товара"
            value={kpi.items.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
            deltaInfo={showDelta ? delta(kpi.items, kpi.prev.items) : undefined}
          />
          <KpiCard
            title="Оборот кассы"
            value={money(
              data?.cash_turnover ??
                kpi.revenue +
                  (data?.corrections.income_total ?? 0) -
                  (data?.corrections.returns_total ?? 0)
            )}
            note="выручка + коррекции прихода − коррекции-возвраты"
          />
        </div>
      )}

      {kpi && (kpi.returns_count ?? 0) > 0 && (
        <p className={hint}>
          Продажи: {money(kpi.sales_revenue ?? kpi.revenue + (kpi.returns_total ?? 0))}
        </p>
      )}

      {/* Сверка «смена»: локальная касса против облака (как в iiko). */}
      {period === 'shift' && ops?.shift && (
        <p className={hint}>
          На этой кассе: {money(ops.shift.totalSales)} · чеков {ops.shift.salesCount}
          {zoneOtherRevenue > 0.01 && ` · ещё ${money(zoneOtherRevenue)} с других касс зоны`}
          {ops.unsentReceipts > 0 &&
            ` · ${ops.unsentReceipts} не отправлено в облако — облачная сумма может быть меньше`}
        </p>
      )}

      {data && dayRows.length > 0 && (
        <div className="mt-3">
          <BlockTitle>Выручка по дням</BlockTitle>
          <BarList
            items={dayRows.map((row) => ({
              label: dayLabel(row.day),
              value: row.revenue,
              hint:
                `${row.receipts} чек.` +
                ((row.returns_total ?? 0) > 0 ? ` · возвраты ${money(row.returns_total ?? 0)}` : '')
            }))}
          />
        </div>
      )}

      {data && (data.top_items.length > 0 || data.by_payment.length > 0) && (
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {data.top_items.length > 0 && (
            <div>
              <BlockTitle>Топ-10 товаров</BlockTitle>
              <BarList
                items={data.top_items.map((row) => ({
                  label: row.name,
                  value: row.revenue,
                  hint: `${row.qty.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ед.`
                }))}
              />
            </div>
          )}
          {data.by_payment.length > 0 && (
            <div>
              <BlockTitle>Оплата</BlockTitle>
              <BarList
                items={data.by_payment.map((row) => ({
                  label: PAYMENT_LABEL[row.method] ?? row.method,
                  value: row.revenue,
                  hint:
                    `${row.receipts} чек.` +
                    ((row.returns_total ?? 0) > 0
                      ? ` · возвраты ${money(row.returns_total ?? 0)}`
                      : '')
                }))}
              />
            </div>
          )}
        </div>
      )}

      {data && (data.by_cashier.length > 1 || data.by_hour.length > 1) && (
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {data.by_cashier.length > 1 && (
            <div>
              <BlockTitle>По кассирам</BlockTitle>
              <BarList
                items={data.by_cashier.map((row) => ({
                  label: row.cashier,
                  value: row.revenue,
                  hint:
                    `${row.receipts} чек.` +
                    ((row.returns_total ?? 0) > 0
                      ? ` · возвраты ${money(row.returns_total ?? 0)}`
                      : '')
                }))}
              />
            </div>
          )}
          {data.by_hour.length > 1 && (
            <div>
              {/* Часы суммарно по дням периода — иначе «00:00» и «23:00»
                  выглядят как один день у смены через полночь. */}
              <BlockTitle>
                {dayRows.length > 1 ? 'Пиковые часы (суммарно за период)' : 'Пиковые часы'}
              </BlockTitle>
              <BarList
                items={data.by_hour.map((row) => ({
                  label: `${String(row.hour).padStart(2, '0')}:00`,
                  value: row.revenue,
                  hint: `${row.receipts} чек.`
                }))}
              />
            </div>
          )}
        </div>
      )}

      {data && data.by_device.length > 1 && (
        <div className="mt-3">
          <BlockTitle>По кассам</BlockTitle>
          <BarList
            items={data.by_device.map((row) => {
              const badge = deviceBadge(row.device_id)
              // Без short все мобильные кассы выглядят одинаково.
              return {
                label: `${badge.label} ${badge.short}`,
                value: row.revenue,
                hint: `${row.receipts} чек.`
              }
            })}
          />
        </div>
      )}

      {data && data.corrections.count > 0 && (
        <p className={hint}>
          Чеки коррекции:
          {(data.corrections.income_count ?? 0) > 0
            ? ` ${data.corrections.income_count} прихода на ${money(
                data.corrections.income_total ?? data.corrections.total
              )}`
            : ''}
          {(data.corrections.returns_count ?? 0) > 0 && (
            <>
              {' '}
              · возвратов прихода {data.corrections.returns_count} на{' '}
              {money(data.corrections.returns_total ?? 0)}
            </>
          )}{' '}
          — в выручку не входят, учтены в показателе «Оборот кассы».
        </p>
      )}

      {ops && (
        <div className="mt-4">
          <BlockTitle>Оперативно</BlockTitle>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <OpsCard
              title="Смена"
              value={!ops.shift ? 'Не открыта' : ops.shiftOpen ? 'Открыта' : 'Закрыта'}
              sub={
                ops.shift
                  ? `№${ops.shift.shiftNumber} · ${ops.shift.cashierName} · ${durationText(
                      ops.shift.openedAt,
                      ops.shift.closedAt ?? Date.now()
                    )}`
                  : 'Откройте смену на кассе'
              }
            />
            <OpsCard
              title="Очередь синхронизации"
              value={pluralRuCount(ops.unsentReceipts, ['чек', 'чека', 'чеков'])}
              sub={
                ops.unsentReceipts + ops.offlineQueue === 0
                  ? 'всё отправлено'
                  : `неотправленных операций: ${ops.offlineQueue}`
              }
            />
            <OpsCard
              title="Каталог"
              value={`${ops.productsLive} поз.`}
              sub={`версия ${ops.activeVersion ?? '—'}${
                ops.syncedAt
                  ? ` · синхронизация ${new Date(ops.syncedAt).toLocaleTimeString('ru-RU', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}`
                  : ''
              }`}
            />
            <OpsCard
              title="Заявки с касс"
              value={pendingCount == null ? '—' : `${pendingCount}`}
              sub={pendingCount ? 'на модерации' : 'нет новых'}
            />
          </div>

          {ops.stockTracked && ops.lowStock.length > 0 && (
            <p className={hint}>
              Заканчиваются (≤ {ops.lowStockThreshold} ед.
              {ops.lowStockOwnRules > 0 ? `, у ${ops.lowStockOwnRules} поз. свой порог` : ''}):{' '}
              {ops.lowStock.map((item) => `${item.name} — ${item.stock} ${item.unit}`).join(' · ')}
            </p>
          )}

          {/* Отклонённые облаком чеки: не ретраятся, но их нельзя «терять» */}
          {ops.rejectedReceipts > 0 && (
            <p className="mt-2 rounded-card border border-warnLine bg-red-50 px-3 py-2 text-small text-dangerDark">
              Облако отклонило {pluralRuCount(ops.rejectedReceipts, ['чек', 'чека', 'чеков'])} — повторно они не отправляются.
              {ops.lastRejectedReason ? ` Причина: ${ops.lastRejectedReason}` : ''}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** Строка оперативного блока: заголовок, значение, пояснение. */
function OpsCard({ title, value, sub }: { title: string; value: string; sub: string }) {
  return (
    <div className="rounded-panel border border-line bg-white p-3">
      <div className="text-small text-muted">{title}</div>
      <div className="text-base2 font-bold text-ink">{value}</div>
      <div className="text-tiny text-muted">{sub}</div>
    </div>
  )
}

/** Заголовок блока внутри карточки. */
function BlockTitle({ children }: { children: string }) {
  return <div className="mb-1 text-sm font-semibold">{children}</div>
}

